import React, { useState, useCallback } from 'react';

interface DividendReportExportProps {
  shares: number;
  sharePrice: number;
  goldPricePerOz: number;
  yearlyDividends: number[];
  fiveYearTotal: number;
  totalPurchase: number;
  roi: number;
}

/**
 * Export dividend calculations as PDF or share via social media
 */
export const DividendReportExport: React.FC<DividendReportExportProps> = ({
  shares,
  sharePrice,
  goldPricePerOz,
  yearlyDividends,
  fiveYearTotal,
  totalPurchase,
  roi
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);

  const generatePDF = useCallback(async () => {
    setIsGenerating(true);
    try {
      // Dynamically import jsPDF
      const { default: jsPDF } = await import('jspdf');
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, 50, 'F');
      
      doc.setTextColor(212, 175, 55);
      doc.setFontSize(24);
      doc.text('Aureus Africa', pageWidth / 2, 25, { align: 'center' });
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.text('Dividend Projection Report', pageWidth / 2, 38, { align: 'center' });
      
      // Reset text color
      doc.setTextColor(0, 0, 0);
      
      // Shareholding Summary
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Shareholding Summary', 20, 65);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');

      const summaryData = [
        ['Number of Shares:', shares.toLocaleString()],
        ['Share Price:', `$${sharePrice.toFixed(2)}`],
        ['Total Purchase:', `$${totalPurchase.toLocaleString()}`],
        ['Gold Price (per oz):', `$${goldPricePerOz.toLocaleString()}`],
        ['Report Date:', new Date().toLocaleDateString()]
      ];
      
      let yPos = 75;
      summaryData.forEach(([label, value]) => {
        doc.text(label, 25, yPos);
        doc.text(value, 100, yPos);
        yPos += 8;
      });
      
      // Dividend Projections
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('5-Year Dividend Projections', 20, yPos + 15);
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      
      yPos += 25;
      const years = ['Year 1 (2026)', 'Year 2 (2027)', 'Year 3 (2028)', 'Year 4 (2029)', 'Year 5 (2030)'];
      
      years.forEach((year, index) => {
        doc.text(year, 25, yPos);
        doc.text(`$${yearlyDividends[index]?.toLocaleString() || '0'}`, 100, yPos);
        yPos += 8;
      });
      
      // Total and Dividend Yield
      doc.setFillColor(16, 185, 129);
      doc.rect(20, yPos + 5, pageWidth - 40, 25, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('5-Year Total Dividends:', 25, yPos + 18);
      doc.text(`$${fiveYearTotal.toLocaleString()}`, pageWidth - 25, yPos + 18, { align: 'right' });
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      yPos += 40;
      doc.text(`Dividend Yield: ${roi.toFixed(1)}%`, 25, yPos);
      
      // Disclaimer
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text('Disclaimer: These projections are estimates based on current gold prices and mining assumptions.', 20, 270);
      doc.text('Actual dividends may vary. This is not financial advice.', 20, 276);
      
      // Save the PDF
      doc.save(`Aureus_Dividend_Report_${shares}_shares.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [shares, sharePrice, goldPricePerOz, yearlyDividends, fiveYearTotal, totalPurchase, roi]);

  const shareText = `I just explored estimated mining dividends with Aureus Africa! ${shares.toLocaleString()} shares — estimated $${fiveYearTotal.toLocaleString()} in production-based dividends over 5 years. Explore yours at aureusafrica.com/calculator`;

  const shareToTwitter = useCallback(() => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  }, [shareText]);

  const shareToLinkedIn = useCallback(() => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://aureusafrica.com/calculator')}`;
    window.open(url, '_blank', 'width=600,height=400');
  }, []);

  const shareToWhatsApp = useCallback(() => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
  }, [shareText]);

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      alert('Copied to clipboard!');
    } catch {
      console.error('Failed to copy');
    }
  }, [shareText]);

  return (
    <div className="dividend-report-export">
      <div className="export-header">
        <h3>📄 Export & Share Your Results</h3>
        <p>Download a professional PDF report or share your projections</p>
      </div>

      <div className="export-actions">
        <button 
          className="export-btn pdf-btn"
          onClick={generatePDF}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <>⏳ Generating...</>
          ) : (
            <>📥 Download PDF Report</>
          )}
        </button>

        <button 
          className="export-btn share-btn"
          onClick={() => setShowShareOptions(!showShareOptions)}
        >
          🔗 Share Results
        </button>
      </div>

      {showShareOptions && (
        <div className="share-options">
          <button className="social-btn twitter" onClick={shareToTwitter}>
            <span>𝕏</span> Twitter
          </button>
          <button className="social-btn linkedin" onClick={shareToLinkedIn}>
            <span>in</span> LinkedIn
          </button>
          <button className="social-btn whatsapp" onClick={shareToWhatsApp}>
            <span>📱</span> WhatsApp
          </button>
          <button className="social-btn copy" onClick={copyToClipboard}>
            <span>📋</span> Copy Link
          </button>
        </div>
      )}
    </div>
  );
};

export default DividendReportExport;

